import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import AddImage from 'images/add_image.png'
import { ImageIcon } from 'lucide-react'
import styled from 'styled-components'

import TWControls from 'components/controls/TWControls'
import { Form, useForm } from 'components/controls/useForm'
import { updateLocation } from 'slices/locationSlice'
import { prepareLocationForSave } from '../utils/prepareLocationForSave'
import Layout from 'components/Layout/Layout'
import * as _ from 'styles/helpers'
import Card from 'components/Card'
import Button from 'components/Button'
import FileUploader from 'components/FileUploader'

const Location_Edit = ({ location }) => {
	const { id } = useParams()
	const navigate = useNavigate()
	const locationId = id
	const dispatch = useDispatch()

	const userLogin = useSelector((state: any) => state.userLogin)
	const { userInfo } = userLogin

	const locationUpdate = useSelector((state: any) => state.locationUpdate)
	const loadingUpdate = locationUpdate?.loading
	const errorUpdate = locationUpdate?.error
	const successUpdate = locationUpdate?.success

	const locationTypes = [
		{ selectValue: 'Commercial Center' },
		{ selectValue: 'Commercial Location' },
		{ selectValue: 'Retail Center' },
		{ selectValue: 'Retail Location' },
	]

	const initialValues = {
		...location,
	}

	const validate = () => {
		let temp: any = { ...errors }

		setErrors({
			...temp,
		})

		return Object.values(temp).every(x => x === '')
	}

	const { values, setValues, errors, setErrors, handleInputChange, resetForm } = useForm(
		initialValues,
		true,
		validate,
	)

	const submitHandler = async e => {
		e.preventDefault()

		// The shared rule (MYD-516): Retail Center and Commercial Center are
		// centers, anything else is a location. This screen used to recognise
		// only "Retail Center" and never turned a center back into a location,
		// so a Commercial Center edited here got no Tenants tab.
		const newLocation = await prepareLocationForSave(values)
		await dispatch(updateLocation(newLocation))
		navigate(`/locations/${locationId}/edit`)
	}

	const formReturn = () => {
		navigate(-1)
	}

	return (
		<>
			<Layout>
				<Layout.Page>
					<Layout.Body>
						<StyledAssetEdit>
							<Form>
								<Card
									className="UserProfile__Card"
									header={{
										title: `Edit: ${values.assetName}`,
										secondary: (
											<Button isOutline onClick={() => formReturn()}>
												Return
											</Button>
										),
									}}
								>
									<div className="grid grid-cols-12 gap-4">
										<div className="col-span-6">
											<TWControls.Input
												size="small"
												name="assetName"
												label="Location Name"
												value={values.assetName}
												onChange={handleInputChange}
											/>
										</div>
										<div className="col-span-3">
											<TWControls.Input
												size="small"
												name="retailer"
												label="Retailer"
												value={values.retailer}
												onChange={handleInputChange}
											/>
										</div>
										<div className="col-span-3">
											<TWControls.SelectType
												className="feed_select"
												name="locationType"
												label="Location Type"
												value={values.locationType}
												onChange={handleInputChange}
												options={locationTypes}
											/>
										</div>
										<div className="col-span-6">
											<TWControls.Input
												size="small"
												name="address"
												label="Address"
												value={values.address}
												onChange={handleInputChange}
											/>
										</div>
										<div className="col-span-3">
											<TWControls.Input
												size="small"
												name="apn"
												label="APN"
												value={values.apn}
												onChange={handleInputChange}
											/>
										</div>
										<div className="col-span-3">
											<TWControls.Input
												size="small"
												name="phoneNumber"
												label="Phone Number"
												value={values.phoneNumber}
												onChange={handleInputChange}
											/>
										</div>

										<div className="col-span-3">
											<TWControls.Input
												size="small"
												name="city"
												label="City"
												value={values.city}
												onChange={handleInputChange}
											/>
										</div>

										<div className="col-span-3">
											<TWControls.Input
												size="small"
												name="stateProvince"
												label="State/Prov"
												value={values.stateProvince}
												onChange={handleInputChange}
											/>
										</div>

										<div className="col-span-3">
											<TWControls.Input
												size="small"
												name="postalCode"
												label="Postal Code"
												value={values.postalCode}
												onChange={handleInputChange}
											/>
										</div>

										<div className="col-span-3">
											<TWControls.Input
												size="small"
												name="country"
												label="Country"
												value={values.country}
												onChange={handleInputChange}
											/>
										</div>

										<div className="col-span-3">
											<TWControls.Input
												size="small"
												name="latitude"
												label="Latitude"
												value={values.latitude}
												onChange={handleInputChange}
											/>
										</div>

										<div className="col-span-3">
											<TWControls.Input
												size="small"
												name="longitude"
												label="Longitude"
												value={values.longitude}
												onChange={handleInputChange}
											/>
										</div>

										<div className="col-span-3">
											<TWControls.Input
												size="small"
												name="totalSqft"
												label="SQFT"
												value={values.totalSqft}
												onChange={handleInputChange}
											/>
										</div>

										<div className="col-span-3">
											<TWControls.Input
												size="small"
												name="estimatedSales"
												label="Sales (est)"
												value={values.estimatedSales}
												onChange={handleInputChange}
											/>
										</div>

										<div className="col-span-3">
											<TWControls.CheckBox
												size="small"
												name="isActive"
												label="Active?"
												value={values.isActive}
												onChange={handleInputChange}
											/>
										</div>

										<div className="col-span-3">
											<TWControls.CheckBox
												size="small"
												name="isInFleet"
												label="Fleet Location?"
												value={values.isInFleet}
												onChange={handleInputChange}
											/>
										</div>

										<div className="col-span-3">
											<TWControls.CheckBox
												size="small"
												name="isPOIOnly"
												label="POI Only?"
												value={values.isPOIOnly}
												onChange={handleInputChange}
											/>
										</div>

										<div className="col-span-3">
											<TWControls.CheckBox
												size="small"
												name="isTenantOnly"
												label="Tenant Only?"
												value={values.isTenantOnly}
												onChange={handleInputChange}
											/>
										</div>
									</div>
									<div className="AssetEdit__Asset">
										{values.image ? (
											<img className="AssetEdit__Image" src={values.image} alt="" />
										) : (
											<img className="newLocation__altimage" src={AddImage} alt="" />
										)}
										<FileUploader onChange={info => { values.image = info.originalUrl }}>
											<ImageIcon className="w-4 h-4" /> {values.image ? 'Update Image' : 'Add Image'}
										</FileUploader>
									</div>

									<div className="AssetEdit__Actions">
										<Button size="small" onClick={submitHandler}>
											Update
										</Button>
									</div>
								</Card>
							</Form>
						</StyledAssetEdit>
					</Layout.Body>
				</Layout.Page>
			</Layout>
		</>
	)
}

const StyledAssetEdit = styled.div`
	max-width: ${_.rem(960)};
	margin: 0 auto;

	.AssetEdit {
		&__Card {
			h2 {
				overflow: hidden;
				white-space: nowrap;
			}
		}

		&__password-buttton {
			${_.MIXINS.vhc}
			margin: ${_.rem(16)} 0 ${_.rem(24)};
		}

		&__Asset,
		&__Actions {
			${_.MIXINS.mod}
			${_.MIXINS.vhc}
			flex-direction: column;
			padding: ${_.rem(16)};
		}

		&__Image {
			width: ${_.rem(250)};
			margin-bottom: ${_.rem(10)};
			border-radius: ${_.rem(5)};
			border: ${_.rem(2)} solid #0073e6;
			margin-bottom: ${_.rem(16)};
		}
	}

	input {
		font-size: ${_.rem(18)};
	}

	.input-group {
		display: flex;
		gap: ${_.rem(16)};
	}

	.input-group-title {
		margin-top: ${_.rem(16)};
		margin-bottom: ${_.rem(16)};
		font-weight: 400;
		font-size: ${_.rem(14)};
		color: ${_.COLORS.gray_500};
	}
`

export default Location_Edit
